import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Edit, Sparkles, Target, Book, Lightbulb } from 'lucide-react';
import { JournalMetrics } from './JournalMetrics';

const promptIcons = {
  morning: {
    gratitude: Sparkles,
    intention: Target
  },
  evening: {
    learnings: Book,
    improvements: Lightbulb
  }
};

export function JournalView({ type, entry, onEdit }) {
  const { t } = useTranslation();

  if (!entry) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
      >
        <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <Book className="w-8 h-8" />
        </div>
        <p>{t('journal.noEntry')}</p>
      </motion.div>
    );
  }

  const getPrompts = () => ({
    morning: [
      {
        id: 'gratitude',
        text: t('journal.prompts.morning.gratitude'),
        bgColor: 'bg-purple-50 dark:bg-purple-900/20',
        borderColor: 'border-purple-100 dark:border-purple-800/30',
        iconColor: 'text-purple-500 dark:text-purple-400'
      },
      {
        id: 'intention',
        text: t('journal.prompts.morning.intention'),
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-100 dark:border-blue-800/30',
        iconColor: 'text-blue-500 dark:text-blue-400'
      }
    ],
    evening: [
      {
        id: 'learnings',
        text: t('journal.prompts.evening.learnings'),
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-100 dark:border-green-800/30',
        iconColor: 'text-green-500 dark:text-green-400'
      },
      {
        id: 'improvements',
        text: t('journal.prompts.evening.improvements'),
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        borderColor: 'border-orange-100 dark:border-orange-800/30',
        iconColor: 'text-orange-500 dark:text-orange-400'
      }
    ]
  });

  const prompts = getPrompts();
  const reflections = entry.content ? JSON.parse(entry.content) : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <JournalMetrics
        type={type}
        values={entry.metrics}
        readonly
      />

      <div className="space-y-4">
        {prompts[type].map((prompt) => {
          const Icon = promptIcons[type][prompt.id];
          return (
            <motion.div
              key={prompt.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-4 ${prompt.bgColor} rounded-xl border ${prompt.borderColor}`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg bg-white dark:bg-gray-800 ${prompt.iconColor}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                    {prompt.text}
                  </h3>
                  <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 backdrop-blur-sm">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {reflections[prompt.id] || t('journal.noReflection')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {onEdit && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onEdit}
          className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 
            dark:from-blue-500 dark:to-blue-600 
            text-white rounded-xl shadow-lg shadow-blue-500/20 
            hover:shadow-blue-500/30 transition-all
            flex items-center justify-center space-x-2"
        >
          <Edit className="w-5 h-5" />
          <span className="font-medium">{t('journal.editButton')}</span>
        </motion.button>
      )}
    </motion.div>
  );
}

export default JournalView;